import { Platform, StyleSheet, TextStyle, ViewStyle } from 'react-native';

import { commonEmptyState } from '../common';
import { badgeStyles } from '../components/badges';
import { cardStyles } from '../components/cards';
import { textStyles } from '../text';
import { colors, spacing, container } from '../theme';

// Styles for work order list screens
export const workOrderListStyles = StyleSheet.create({
	container: {
		...container.screen,
	},
	listContent: {
		padding: spacing.m,
		paddingBottom: 80, // Extra padding for the FAB
	},
	filterBar: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		paddingHorizontal: spacing.m,
		paddingVertical: spacing.s,
		backgroundColor: colors.surface,
		borderBottomWidth: 1,
		borderBottomColor: colors.outline,
	},
	clearFiltersButton: {
		borderRadius: 8,
	},
	workOrderItem: {
		...cardStyles.card,
	},
	workOrderHeader: {
		...cardStyles.cardHeader,
	},
	workOrderTitle: {
		...cardStyles.cardTitle,
	},
	workOrderDate: {
		...cardStyles.cardDate,
	},
	workOrderDescription: {
		...cardStyles.cardDescription,
	},
	workOrderFooter: {
		...cardStyles.cardFooter,
	},
	badge: {
		...badgeStyles.badge,
	},
	locationContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		marginLeft: 'auto',
	},
	locationText: {
		fontSize: 12,
		color: colors.textSecondary,
		marginLeft: 4,
		maxWidth: 120,
	},
	assignmentContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		marginLeft: 'auto',
		marginRight: 8,
	},
	assignmentText: {
		fontSize: 12,
		color: colors.textSecondary,
		marginLeft: 4,
		maxWidth: 150,
	},
	emptyContainer: {
		...commonEmptyState, // Shared empty state style
	},
	emptyText: {
		fontSize: 18,
		color: colors.textSecondary,
		marginTop: spacing.m,
	},
	emptySubtext: {
		fontSize: 14,
		color: colors.textLight,
		marginTop: spacing.s,
		textAlign: 'center',
	},
});

// Styles for work order detail screens
export const workOrderDetailStyles = StyleSheet.create({
	container: {
		...container.scrollView,
	},
	headerSection: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.s,
		marginLeft: 10,
	},
	title: {
		flex: 1,
		marginBottom: spacing.m,
		fontWeight: 'bold',
	},
	statusRow: {
		flexDirection: 'row',
		marginBottom: spacing.l,
	},
	statusBadge: {
		...badgeStyles.badge,
	},
	statusText: {
		...badgeStyles.badgeText,
	},
	priorityBadge: {
		...badgeStyles.badge,
	},
	priorityText: {
		...badgeStyles.badgeText,
	},
	section: {
		marginVertical: spacing.m,
	},
	/**
	 * Maintenance's internal completion note (#496).
	 *
	 * Visually recessed — muted rule and background — so it reads as reference material
	 * beside the customer-facing description above it, not as a second answer competing
	 * with it. Only rendered for the roles the API sends the raw field to.
	 */
	internalSection: {
		backgroundColor: '#f5f7f8',
		borderLeftWidth: 3,
		borderLeftColor: '#b0bec5',
		borderRadius: 6,
		paddingHorizontal: spacing.s,
		paddingVertical: spacing.s,
	},
	sectionHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.s,
	},
	detailRow: {
		flexDirection: 'row',
		marginVertical: spacing.xs,
	},
	detailLabel: {
		width: 130,
		paddingRight: spacing.s,
		fontWeight: 'bold',
		color: colors.textSecondary,
		fontSize: 16,
	},
	detailValue: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		gap: 4,
		fontSize: 16,
	},
	divider: {
		marginVertical: spacing.l,
	},
	actionsContainer: {
		flexDirection: 'row',
		justifyContent: 'space-around',
		marginBottom: spacing.xl,
	},
	actionsContainerStacked: {
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: spacing.s,
	},
	button: {
		minWidth: 120,
	},
	linkText: {
		color: colors.primary,
		textDecorationLine: 'underline',
	},

	printContainer: {
		flex: 1,
		backgroundColor: '#fff',
		margin: 'auto',
		width: 800,
	} as ViewStyle,
	printableContent: {
		padding: 16,
		backgroundColor: '#fff',
		...(Platform.OS === 'web' ? {
			// Using percent for cross-platform compatibility
			width: '100%',
			minHeight: Platform.OS === 'web' ? '100%' : undefined,
		} : {}),
	} as ViewStyle,
	loadingText: {
		textAlign: 'center',
		margin: 20,
		fontSize: 16,
	},
	buttonContainer: {
		paddingHorizontal: 16,
		paddingVertical: 10,
		borderBottomWidth: 1,
		borderBottomColor: '#ddd',
		backgroundColor: '#f5f5f5',
	} as ViewStyle,
	header: {
		alignItems: 'center',
		marginBottom: 0,
		flex: 1,
		flexDirection: 'row',
		justifyContent: 'space-between',
	} as ViewStyle,
	orderNumber: {
		marginTop: 10,
		fontSize: 22,
		fontWeight: 'bold',
		color: '#FF6666',
	} as TextStyle,
	infoGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginBottom: 0,
	} as ViewStyle,
	infoBox: {
		flex: 1,
		minWidth: 250,
		padding: 10,
		borderWidth: 1,
		borderColor: '#ddd',
		borderRadius: 5,
		marginRight: 10,
		marginBottom: 10,
	} as ViewStyle,
	sectionTitle: {
		fontSize: 18,
		fontWeight: 'bold',
		marginBottom: 8,
		paddingBottom: 5,
		borderBottomWidth: 1,
		borderBottomColor: '#eee',
	},
	infoRow: {
		marginBottom: 5,
	},
	infoLabel: {
		fontWeight: 'bold',
	},
	highlightText: {
		...textStyles.highlightText,
	},
	sectionContent: {
		marginTop: 5,
	},
	tableContainer: {
		overflow: 'visible',
	} as ViewStyle,
	tableHeader: {
		flexDirection: 'row',
		backgroundColor: '#f2f2f2',
		borderBottomWidth: 1,
		borderBottomColor: '#ddd',
	} as ViewStyle,
	tableHeaderCell: {
		padding: 8,
		fontWeight: 'bold',
	},
	tableRow: {
		flexDirection: 'row',
	} as ViewStyle,
	tableCell: {
		padding: 8,
	},
	evenRow: {
		backgroundColor: '#fff',
	} as ViewStyle,
	oddRow: {
		backgroundColor: '#f9f9f9',
	} as ViewStyle,
	dateCell: {
		flex: 1.5,
	},
	descCell: {
		flex: 3,
	},
	hoursCell: {
		flex: 1,
		textAlign: 'right',
	},
	qtyCell: {
		flex: 1,
	},
	rateCell: {
		flex: 1.5,
	},
	amountCell: {
		flex: 1.5,
		textAlign: 'right',
		fontWeight: 'bold',
		color: '#1a1a1a',
		fontVariant: ['tabular-nums'],
		fontFamily: 'Menlo, Consolas, monospace',
		backgroundColor: '#e6f2ff',
		borderWidth: 1,
		borderColor: '#b3c6e7',
		borderRadius: 3,
		paddingVertical: 4,
		paddingHorizontal: 8,
	},
	totalCell: {
		textAlign: 'right',
	},
	emptyMessage: {
		fontStyle: 'italic',
		color: '#666',
		marginTop: 5,
	},
	totals: {
		alignItems: 'flex-end',
	} as ViewStyle,
	totalTableRow: {
		borderTopWidth: 1,
	},
	totalRow: {
		flexDirection: 'row',
		marginBottom: 5,
		minHeight: 25,
	},
	totalLabel: {
		fontWeight: 'bold',
		marginRight: 5,
	},
	signatureSection: {
		flexDirection: 'row',
		marginTop: 25,
		justifyContent: 'space-between',
	} as ViewStyle,
	signatureBox: {
		width: '45%',
		alignItems: 'center',
	} as ViewStyle,
	// The rule people sign ON. The height is FIXED and tall enough for a signature image
	// so every block's rule lands at the same level (#483) — when only signed blocks
	// reserved the image's height, an unsigned neighbour's rule floated 40px higher.
	signatureLine: {
		alignItems: 'center',
		justifyContent: 'flex-end',
		borderBottomWidth: 1,
		borderBottomColor: '#000',
		width: '100%',
		height: 44,
	} as ViewStyle,
	signatureImage: {
		height: 40,
		width: 180,
	},
	// Printed name and date, BELOW the rule — identification, never the signature itself.
	signaturePrintedRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		paddingHorizontal: 30,
		width: '100%',
		marginBottom: 6,
	} as ViewStyle,
	signatureLabel: {
		textAlign: 'center',
	},
	// The stand-in signature: a typed name in a cursive face ON the rule, so a form
	// nobody has uploaded a signature for still reads as signed.
	signatureText: {
		fontSize: 16,
		fontFamily: 'Cursive, Serif',
		fontStyle: 'italic',
		textAlign: 'center',
	} as TextStyle,
	// No fontFamily, so the printed row matches the rest of the form.
	signaturePrintedText: {
		fontSize: 14,
	} as TextStyle,
	// Attachment styles
	appendixSection: {
		marginTop: 50,
	} as ViewStyle,
	// The appendix always begins a new sheet so the signed form stays one page (#498).
	appendixPageBreak: {
		...(Platform.OS === 'web' ? {
			breakBefore: 'page',
			pageBreakBefore: 'always',
		} : {}),
	} as ViewStyle,
	appendixTitle: {
		fontSize: 24,
		fontWeight: 'bold',
		textAlign: 'center',
		marginBottom: 20,
	},
	attachmentSection: {
		marginBottom: 30,
		...(Platform.OS === 'web' ? {
			breakInside: 'avoid',
			pageBreakInside: 'avoid',
		} : {}),
	} as ViewStyle,
	attachmentTypeTitle: {
		fontSize: 18,
		fontWeight: 'bold',
		marginBottom: 10,
		paddingBottom: 5,
		borderBottomWidth: 1,
		borderBottomColor: '#eee',
	},
	attachmentGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
	} as ViewStyle,
	attachmentItem: {
		width: '100%',
		margin: 10,
		padding: 10,
		borderWidth: 1,
		borderColor: '#ddd',
		borderRadius: 5,
	} as ViewStyle,
	attachmentImage: {
		width: '100%',
		height: 350,
		marginBottom: 8,
		backgroundColor: '#f5f5f5',
		...(Platform.OS === 'web' ? {
			breakInside: 'avoid',
			pageBreakInside: 'avoid',
		} : {}),
	},
	attachmentImageText: {
		fontSize: 14,
		fontWeight: 'bold',
		backgroundColor: '#66666666',
		color: '#ffffff',
		textAlign: 'right',
		position: 'absolute',
		bottom: 5,
		right: 5,
		padding: 4,
		borderRadius: 4,
	},
	attachmentFileIcon: {
		width: '100%',
		height: 150,
		backgroundColor: '#f5f5f5',
		justifyContent: 'center',
		alignItems: 'center',
		marginBottom: 8,
	} as ViewStyle,
	attachmentFileType: {
		fontSize: 16,
		fontWeight: 'bold',
		textTransform: 'uppercase',
	},
	attachmentFileName: {
		fontSize: 12,
		marginBottom: 5,
	},
	attachmentDescription: {
		fontSize: 12,
		fontStyle: 'italic',
		color: '#666',
	},
	attachmentImageContainer: {
		width: '100%',
		height: 350,
		marginBottom: 8,
		backgroundColor: '#f5f5f5',
	} as ViewStyle,
});
